/*!
 * Adapted from arsh342/careercompass, animated-characters.tsx
 * commit 8c905a4f65e0a43801261b5009a0b92226b62222.
 * MIT License — Copyright (c) 2025 arsh342
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Component, useEffect, useRef, useState } from 'react';

// Upstream silhouettes, relative placement and face geometry; only the palette changes.
const parts = [
  {name:'purple',left:70,width:180,height:400,eyeLeft:45,eyeTop:40,eyeSize:18,pupilSize:7,gap:32},
  {name:'black',left:240,width:120,height:310,eyeLeft:26,eyeTop:32,eyeSize:16,pupilSize:6,gap:24},
  {name:'orange',left:0,width:240,height:200,eyeLeft:82,eyeTop:90,eyeSize:12,pupilSize:12,gap:32},
  {name:'yellow',left:310,width:140,height:230,eyeLeft:52,eyeTop:40,eyeSize:12,pupilSize:12,gap:24},
];
const clamp = (value,min,max) => Math.max(min,Math.min(max,value));

function EyeBall({size,pupilSize,position}) {
  return <span className={size===pupilSize?'login-character-eye plain':'login-character-eye'}
    style={{width:size,height:size}}><span className="login-character-pupil"
      style={{width:pupilSize,height:pupilSize,transform:`translate(${position[0]}px,${position[1]}px)`}} /></span>;
}

function Characters({focus='none',passwordVisible=false,phase='idle'}) {
  const stage = useRef(null);
  const [scale,setScale] = useState(1);
  const [pointer,setPointer] = useState(null);
  const [enabled,setEnabled] = useState(false);
  const [isLookingAtEachOther,setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking,setIsPurplePeeking] = useState(false);
  const lookConsumed=useRef(false);
  const peekConsumed=useRef(false);
  const waiting = ['submitting','checking','unknown','redirecting'].includes(phase);
  const averted = focus==='password' || passwordVisible || phase==='challenge';
  const tracking = enabled && !waiting && !averted;

  useEffect(() => {
    const media=window.matchMedia('(prefers-reduced-motion: reduce)');
    const update=()=>setEnabled(!media.matches && !document.hidden);
    update();media.addEventListener('change',update);document.addEventListener('visibilitychange',update);
    return ()=>{media.removeEventListener('change',update);document.removeEventListener('visibilitychange',update);};
  },[]);
  useEffect(() => {
    const measure=()=>setScale(stage.current?.getBoundingClientRect().width/550 || 1);
    measure();
    if(typeof ResizeObserver==='undefined'){window.addEventListener('resize',measure);return()=>window.removeEventListener('resize',measure);}
    const observer=new ResizeObserver(measure);observer.observe(stage.current);
    return()=>observer.disconnect();
  },[]);
  useEffect(() => {
    setPointer(null);
    if(!tracking)return;
    let frame=0,latest;
    const move=event=>{
      if(event.pointerType && event.pointerType!=='mouse')return;
      latest={x:event.clientX,y:event.clientY};
      if(frame)return;
      frame=requestAnimationFrame(()=>{
        frame=0;const box=stage.current?.getBoundingClientRect();if(!box)return;
        setPointer({x:(latest.x-box.left)/scale,y:(latest.y-box.top)/scale});
      });
    };
    window.addEventListener('pointermove',move,{passive:true});
    return()=>{window.removeEventListener('pointermove',move);cancelAnimationFrame(frame);};
  },[tracking,scale]);
  useEffect(() => {
    setIsLookingAtEachOther(false);
    if(focus!=='email'){lookConsumed.current=false;return;}
    if(lookConsumed.current)return;
    lookConsumed.current=true;
    if(!enabled || waiting || averted)return;
    setIsLookingAtEachOther(true);
    const timer=setTimeout(()=>setIsLookingAtEachOther(false),800);
    return()=>clearTimeout(timer);
  },[focus,enabled,waiting,averted]);
  useEffect(() => {
    setIsPurplePeeking(false);
    if(focus!=='password' || !passwordVisible){peekConsumed.current=false;return;}
    if(peekConsumed.current)return;
    peekConsumed.current=true;
    if(!enabled || waiting)return;
    // One opportunity per explicit focus/visibility interaction, consumed even if cancelled.
    let finish;
    const start=setTimeout(()=>{
      setIsPurplePeeking(true);finish=setTimeout(()=>setIsPurplePeeking(false),650);
    },2200);
    return()=>{clearTimeout(start);clearTimeout(finish);};
  },[enabled,waiting,focus,passwordVisible]);

  return <div ref={stage} className="login-characters-stage" aria-hidden="true"
    data-motion={enabled?'active':'paused'} data-pose={averted?'averted':waiting?'waiting':focus==='email'?'attentive':'idle'}>
    <div className="login-characters-canvas" style={{transform:`scale(${scale})`}}>
      {parts.map(part=>{
        const activeHeight=part.name==='purple' && (focus==='email'||averted)?440:part.height;
        const dx=pointer?pointer.x-(part.left+part.width/2):0;
        const dy=pointer?pointer.y-(440-activeHeight+activeHeight/3):0;
        const faceX=clamp(dx/20,-15,15),faceY=clamp(dy/30,-10,10);
        let skew=clamp(-dx/120,-6,6),translate=0;
        let eyeLeft=part.eyeLeft+faceX,eyeTop=part.eyeTop+faceY;
        let look=null;
        if(averted){
          skew=0;eyeLeft=part.name==='purple'?20:part.name==='black'?10:part.name==='orange'?50:20;
          eyeTop=part.name==='purple'?35:part.name==='black'?28:part.name==='orange'?85:40;
          look=part.name==='purple' && isPurplePeeking?[4,5]:[-4,-4];
        } else if(isLookingAtEachOther && part.name==='purple'){
          skew-=12;translate=40;eyeLeft=55;eyeTop=65;look=[3,4];
        } else if(isLookingAtEachOther && part.name==='black'){
          skew=skew*1.5+10;translate=20;eyeLeft=32;eyeTop=12;look=[0,-4];
        }
        if(waiting && !averted){skew=0;look=[0,3];}
        if(phase==='error' && !averted){eyeTop+=6;look=[0,3];}
        const angle=Math.atan2(dy,dx);
        const distance=pointer?Math.min(Math.hypot(dx,dy),5):0;
        const pupil=look || [Math.cos(angle)*distance,Math.sin(angle)*distance];
        return <div key={part.name} className={`login-character ${part.name}`}
          style={{left:part.left,width:part.width,height:activeHeight,transform:`skewX(${skew}deg) translateX(${translate}px)`}}>
          <div className="login-character-eyes" style={{left:eyeLeft,top:eyeTop,gap:part.gap}}>
            <EyeBall size={part.eyeSize} pupilSize={part.pupilSize} position={pupil}/>
            <EyeBall size={part.eyeSize} pupilSize={part.pupilSize} position={pupil}/>
          </div>
          {part.name==='yellow' && <span className="login-character-mouth" style={{left:eyeLeft-12,top:eyeTop+40}}/>}
        </div>;
      })}
    </div>
  </div>;
}

class CharacterBoundary extends Component {
  state={failed:false};
  static getDerivedStateFromError(){return {failed:true};}
  render(){return this.state.failed?<div className="login-characters-fallback" aria-hidden="true">{parts.map(part=><span key={part.name} className={part.name}/>)}</div>:this.props.children;}
}

export function LoginCharacters(props) {
  return <CharacterBoundary><Characters {...props}/></CharacterBoundary>;
}
